import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,   
    InputLabel,   
    MenuItem,   
    Modal,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useGetItemsQuery, usePostItemMutation } from '../../redux/residentPortalApi';
import developmentsData from '../../resident-portal';


interface ItemState {
    description: string;
    unitPrice: number;
}

const initialItemState: ItemState = {
    description: '',
    unitPrice: 0,
};



const Products = () => {


    const [open, setOpen] = useState(false);
    const [newItem, setNewItem] = useState<ItemState>(initialItemState);
    
    
    const [selectedDevelopment, setSelectedDevelopment] = useState('');
    const [selectedPhase, setSelectedPhase] = useState('');
    const [selectedType, setSelectedType] = useState('');

  
    const [phaseOptions, setPhaseOptions] = useState<string[]>([]);
    const [typeOptions, setTypeOptions] = useState<string[]>([]);

    const { data: items, isLoading, isError } = useGetItemsQuery();
    const [postItem] = usePostItemMutation();

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setNewItem(prev => ({ ...prev, [name]: value }));
    };

    
    const handleDevelopmentChange = (event: SelectChangeEvent) => {
        const development = event.target.value as string;
        setSelectedDevelopment(development);
    
        
        const phases = developmentsData.propertyMetrics
            .filter(item => item.development === development)
            .map(item => item.phase)
            .filter((phase, index, self): phase is string => !!phase && self.indexOf(phase) === index); 
    
        setPhaseOptions(phases);
        setSelectedPhase(''); 
        setTypeOptions([]); 
        setSelectedType('');
    };

    
const handlePhaseChange = (event: SelectChangeEvent) => {
    const phase = event.target.value as string;
    setSelectedPhase(phase);

    
    const types = developmentsData.propertyMetrics
        .filter(item => item.development === selectedDevelopment && item.phase === phase)
        .map(item => item.type)
        .filter((type, index, self): type is string => !!type && self.indexOf(type) === index); 

    setTypeOptions(types);
    setSelectedType('');
};

    
const handleTypeChange = (event: SelectChangeEvent) => {
    setSelectedType(event.target.value as string);
};

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const item = {
            description: newItem.description,
            unitPrice: typeof newItem.unitPrice === 'string' ? parseFloat(newItem.unitPrice) : newItem.unitPrice,
            development: selectedDevelopment,
            phase: selectedPhase,
            type: selectedType
        };

        try {
            const response = await postItem({ item }).unwrap();
            console.log('Item added:', response);
            handleClose();
        } catch (err) {
            console.error('Failed to add Item:', err);
        }
    };

    if (isLoading) return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '50vh',
            gap: 2
        }}>
            <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
            <Typography variant="h6" color="primary" sx={{ mt: 2, fontWeight: 'medium' }}>
                Loading Items...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                We're preparing your product data. This will only take a moment.
            </Typography>
        </Box>
    );
    
    if (isError) return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            p: 3, 
            bgcolor: 'error.light', 
            borderRadius: 2,
            mt: 2
        }}>
            <Typography variant="h6" color="error.dark">
                Error loading Items
            </Typography>
            <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
                Please try refreshing the page or contact support if the problem persists.
            </Typography>
        </Box>
    );

    return (
        <>
            <Button variant="contained" color="primary" onClick={handleOpen}>
                Add New Product
            </Button>
            <br /><br />
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Development</TableCell>
                            <TableCell>Phase</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Unit Price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items?.map((prop) => (
                            <TableRow key={prop.id}>
                                <TableCell>{prop.development}</TableCell>
                                <TableCell>{prop.phase}</TableCell>
                                <TableCell>{prop.type}</TableCell>
                                <TableCell>{prop.description}</TableCell>
                                <TableCell>{prop.unitPrice}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="add-Item-modal-title"
                aria-describedby="add-Item-modal-description"
            >
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4
                }}>
                    <Typography id="add-Item-modal-title" variant="h6" component="h2">
                        Add New Product
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            
                            <FormControl fullWidth>
                                <InputLabel id="development-label">Development</InputLabel>
                                <Select
                                    labelId="development-label"
                                    value={selectedDevelopment}
                                    onChange={handleDevelopmentChange}
                                >
                                    {developmentsData.propertyMetrics
                                        .map(item => item.development)
                                        .filter((development, index, self) => self.indexOf(development) === index) 
                                        .map(development => (
                                            <MenuItem key={development} value={development}>
                                                {development}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>

                            
                            <FormControl fullWidth>
                                <InputLabel id="phase-label">Phase</InputLabel>
                                <Select
                                    labelId="phase-label"
                                    value={selectedPhase}
                                    onChange={handlePhaseChange}
                                    disabled={!phaseOptions.length}
                                >
                                    {phaseOptions.map(phase => (
                                        <MenuItem key={phase} value={phase}>
                                            {phase}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            
                            <FormControl fullWidth>
                                <InputLabel id="type-label">Type</InputLabel>
                                <Select
                                    labelId="type-label"
                                    value={selectedType}
                                    onChange={handleTypeChange}
                                    disabled={!typeOptions.length}
                                >
                                    {typeOptions.map(type => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                margin="normal"
                                fullWidth
                                label="Unitprice"
                                name="unitPrice"
                                type="number"
                                onChange={handleChange}
                            />
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Description"
                                name="description"
                                multiline
                                rows={4}
                                onChange={handleChange}
                            />
                            <Button type="submit" color="primary" variant="contained">
                                Submit
                            </Button>
                        </Stack>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

export default Products;

